(() => {
  if (window.ShrineCartQuantityRepairV3) return;
  window.ShrineCartQuantityRepairV3 = true;

  const LOCK_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop'
  ];
  const pendingLines = new Map();
  const debounceMs = 180;

  const cartChangeUrl = () => window.routes?.cart_change_url || '/cart/change.js';
  const cartUrl = () => window.routes?.cart_url || '/cart';

  const numberValue = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const limits = (input, allowZero = false) => {
    const min = allowZero ? 0 : numberValue(input.min || input.dataset.min, 1);
    const max = input.max ? numberValue(input.max, Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
    const step = Math.max(numberValue(input.step, 1), 1);
    return { min, max, step };
  };

  const normalizeQuantity = (input, value, allowZero = false) => {
    const { min, max } = limits(input, allowZero);
    return Math.max(min, Math.min(max, numberValue(value, min)));
  };

  const steppedQuantity = (input, direction) => {
    const current = normalizeQuantity(input, input.value);
    const { min, max, step } = limits(input);
    const candidate = direction === 'plus' ? current + step : current - step;
    return Math.max(min, Math.min(max, candidate));
  };

  const dispatchQuantityEvents = (input) => {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const productFormForInput = (input) => {
    const formId = input.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) return form;
    }

    const productInfo = input.closest('product-info, .product__info-container, .section-featured-product');
    return productInfo?.querySelector('form[data-type="add-to-cart-form"]') || null;
  };

  const productInputForForm = (form) => {
    if (!form) return null;
    const formId = form.id;
    const escapedId = window.CSS?.escape ? CSS.escape(formId) : formId.replace(/(["'\\.#:[\],=])/g, '\\$1');
    const external = formId
      ? document.querySelector(`input.quantity__input[name="quantity"][form="${escapedId}"]`)
      : null;

    if (external && !external.closest('cart-drawer, cart-items')) return external;

    const productInfo = form.closest('product-info, .product__info-container, .section-featured-product');
    return productInfo?.querySelector('input.quantity__input[name="quantity"]:not([name="updates[]"])') || null;
  };

  const bindProductForm = (form, input) => {
    if (!form || !input) return;

    let hidden = form.querySelector('input[data-shrine-product-quantity]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.dataset.shrineProductQuantity = 'true';
      form.appendChild(hidden);
    }

    hidden.value = String(normalizeQuantity(input, input.value));

    if (form.dataset.shrineQuantityBound !== 'true') {
      form.dataset.shrineQuantityBound = 'true';
      form.addEventListener('formdata', (event) => {
        const currentInput = productInputForForm(form);
        const currentHidden = form.querySelector('input[data-shrine-product-quantity]');
        const quantity = currentInput
          ? normalizeQuantity(currentInput, currentInput.value)
          : numberValue(currentHidden?.value, 1);
        event.formData.set('quantity', String(quantity));
      });
    }
  };

  const syncProductQuantity = (input) => {
    if (!input) return;
    const quantity = normalizeQuantity(input, input.value);
    input.value = String(quantity);
    bindProductForm(productFormForInput(input), input);
  };

  const handleProductQuantityButton = (event, button) => {
    const wrapper = button.closest('quantity-input');
    const input = wrapper?.querySelector('input.quantity__input[name="quantity"]');
    if (!input || input.closest('cart-drawer, cart-items') || input.disabled) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const quantity = steppedQuantity(input, button.name);
    input.value = String(quantity);
    syncProductQuantity(input);
    dispatchQuantityEvents(input);
    return true;
  };

  const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  const sectionFromHtml = (html, sectionId) => {
    if (!html) return null;
    const parsed = parseHtml(html);
    return parsed.getElementById(`shopify-section-${sectionId}`) || parsed.body;
  };

  const importNode = (node) => document.importNode(node, true);

  const replaceNode = (current, incoming) => {
    if (!current || !incoming) return false;
    current.replaceWith(importNode(incoming));
    return true;
  };

  const renderCartIcon = (html) => {
    if (!html) return;
    const section = sectionFromHtml(html, 'cart-icon-bubble');
    const current = document.getElementById('cart-icon-bubble');
    if (!current || !section) return;

    const incoming = section.querySelector('#cart-icon-bubble');
    current.innerHTML = incoming ? incoming.innerHTML : section.innerHTML;
  };

  const renderDrawerInPlace = (html) => {
    if (!html) return;

    const section = sectionFromHtml(html, 'cart-drawer');
    const incomingDrawer = section?.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    if (!incomingDrawer || !currentDrawer) return;

    const wasActive = currentDrawer.classList.contains('active');
    const bodyScroll = currentDrawer.querySelector('.cart-drawer__body')?.scrollTop || 0;
    const innerScroll = currentDrawer.querySelector('.drawer__inner')?.scrollTop || 0;
    const incomingEmpty = incomingDrawer.classList.contains('is-empty');
    const wasEmpty = currentDrawer.classList.contains('is-empty');
    const currentInner = currentDrawer.querySelector('.drawer__inner');
    const incomingInner = incomingDrawer.querySelector('.drawer__inner');

    currentDrawer.classList.toggle('is-empty', incomingEmpty);

    if (incomingEmpty || wasEmpty !== incomingEmpty) {
      if (currentInner && incomingInner) currentInner.innerHTML = incomingInner.innerHTML;
    } else {
      replaceNode(
        currentDrawer.querySelector('.drawer__header'),
        incomingDrawer.querySelector('.drawer__header')
      );
      replaceNode(
        currentDrawer.querySelector('.cart-drawer__body'),
        incomingDrawer.querySelector('.cart-drawer__body')
      );
      replaceNode(
        currentDrawer.querySelector('.drawer__footer'),
        incomingDrawer.querySelector('.drawer__footer')
      );
    }

    if (wasActive) {
      currentDrawer.classList.add('active');
      currentDrawer.style.pointerEvents = '';
      document.body?.classList.add('overflow-hidden');
    }

    window.requestAnimationFrame(() => {
      const newBody = currentDrawer.querySelector('.cart-drawer__body');
      const newInner = currentDrawer.querySelector('.drawer__inner');
      if (newBody) newBody.scrollTop = bodyScroll;
      if (newInner) newInner.scrollTop = innerScroll;
    });
  };

  const renderCartPage = (sections) => {
    if (!sections) return;

    const itemsHtml = sections['main-cart-items'];
    if (itemsHtml) {
      const section = sectionFromHtml(itemsHtml, 'main-cart-items');
      replaceNode(
        document.getElementById('shopify-section-main-cart-items'),
        section?.id === 'shopify-section-main-cart-items' ? section : null
      );
    }

    const footerHtml = sections['main-cart-footer'];
    if (footerHtml) {
      const section = sectionFromHtml(footerHtml, 'main-cart-footer');
      replaceNode(
        document.getElementById('shopify-section-main-cart-footer'),
        section?.id === 'shopify-section-main-cart-footer' ? section : null
      );
    }
  };

  const requestedSections = () => {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    if (document.querySelector('cart-items, #main-cart-items')) {
      sections.push('main-cart-items', 'main-cart-footer');
    }
    return sections;
  };

  const fetchRenderedSections = async (sectionIds) => {
    const separator = window.location.pathname.includes('?') ? '&' : '?';
    const url = `${window.location.pathname || cartUrl()}${separator}sections=${encodeURIComponent(sectionIds.join(','))}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return response.json().catch(() => null);
  };

  const clearCartErrors = (line, inDrawer) => {
    const selectors = inDrawer
      ? [`#CartDrawer-LineItemError-${line} .cart-item__error-text`, '#CartDrawer-CartErrors']
      : [`#Line-item-error-${line} .cart-item__error-text`, '#cart-errors'];

    selectors.forEach((selector) => {
      const element = document.querySelector(selector);
      if (element) element.textContent = '';
    });
  };

  const showCartError = (line, inDrawer, message) => {
    const selectors = inDrawer
      ? [`#CartDrawer-LineItemError-${line} .cart-item__error-text`, '#CartDrawer-CartErrors']
      : [`#Line-item-error-${line} .cart-item__error-text`, '#cart-errors'];
    const target = selectors.map((selector) => document.querySelector(selector)).find(Boolean);
    if (target) {
      target.textContent = message || window.cartStrings?.error || 'There was an error while updating your cart. Please try again.';
    }
  };

  const setLineBusy = (lineRoot, busy) => {
    if (!lineRoot) return;
    lineRoot.classList.toggle('is-updating', busy);
    lineRoot.setAttribute('aria-busy', busy ? 'true' : 'false');
    lineRoot.querySelectorAll('quantity-input button.quantity__button, cart-remove-button button').forEach((button) => {
      button.disabled = busy;
    });
    const overlay = lineRoot.querySelector(':scope > .loading-overlay');
    overlay?.classList.toggle('hidden', !busy);
  };

  const renderCartResponse = async (data, sectionIds) => {
    let sections = data?.sections || null;
    if (!sections) sections = await fetchRenderedSections(sectionIds);
    if (!sections) return;

    renderDrawerInPlace(sections['cart-drawer']);
    renderCartIcon(sections['cart-icon-bubble']);
    renderCartPage(sections);
  };

  const performCartChange = async ({ line, quantity, input, lineRoot, inDrawer, committed }) => {
    const sectionIds = requestedSections();
    setLineBusy(lineRoot, true);
    clearCartErrors(line, inDrawer);

    try {
      const response = await fetch(cartChangeUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionIds,
          sections_url: window.location.pathname || cartUrl()
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || window.cartStrings?.error);
      }

      await renderCartResponse(data, sectionIds);
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: data }
      }));
    } catch (error) {
      if (input?.isConnected && committed != null) input.value = String(committed);
      setLineBusy(lineRoot, false);
      showCartError(line, inDrawer, error?.message);
    }
  };

  const queueCartChange = (payload) => {
    const scope = payload.inDrawer ? 'drawer' : 'page';
    const key = `${scope}:${payload.line}`;
    const previous = pendingLines.get(key);
    if (previous) window.clearTimeout(previous);

    const timer = window.setTimeout(() => {
      pendingLines.delete(key);
      performCartChange(payload);
    }, debounceMs);
    pendingLines.set(key, timer);
  };

  const cartLineContext = (target) => {
    const inDrawer = Boolean(target.closest('cart-drawer'));
    const lineRoot = inDrawer
      ? target.closest('[id^="CartDrawer-Item-"][data-index]')
      : target.closest('[id^="CartItem-"]');
    const input = lineRoot?.querySelector('input.quantity__input[name="updates[]"][data-index]') || null;
    const line = Number.parseInt(
      input?.dataset.index || target.closest('cart-remove-button')?.dataset.index || lineRoot?.dataset.index || '',
      10
    );
    return { inDrawer, lineRoot, input, line };
  };

  const handleCartQuantityButton = (event, button) => {
    const { inDrawer, lineRoot, input, line } = cartLineContext(button);
    if (!input || !Number.isInteger(line) || line < 1 || input.disabled) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const committed = numberValue(input.dataset.shrineCommittedQuantity, numberValue(input.value, 1));
    if (!input.dataset.shrineCommittedQuantity) input.dataset.shrineCommittedQuantity = String(committed);

    const quantity = steppedQuantity(input, button.name);
    if (quantity === normalizeQuantity(input, input.value)) return true;

    input.value = String(quantity);
    clearCartErrors(line, inDrawer);
    queueCartChange({ line, quantity, input, lineRoot, inDrawer, committed });
    return true;
  };

  const handleCartRemove = (event, removeControl) => {
    const { inDrawer, lineRoot, input, line } = cartLineContext(removeControl);
    if (!Number.isInteger(line) || line < 1) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const committed = input ? numberValue(input.value, 1) : null;
    clearCartErrors(line, inDrawer);
    queueCartChange({ line, quantity: 0, input, lineRoot, inDrawer, committed });
    return true;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('quantity-input button.quantity__button[name="plus"], quantity-input button.quantity__button[name="minus"]');
    if (button) {
      if (button.closest('cart-drawer, cart-items')) {
        handleCartQuantityButton(event, button);
      } else {
        handleProductQuantityButton(event, button);
      }
      return;
    }

    const removeControl = event.target.closest('cart-remove-button');
    if (removeControl) handleCartRemove(event, removeControl);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('input.quantity__input');
    if (!input) return;

    if (!input.closest('cart-drawer, cart-items') && input.name === 'quantity') {
      syncProductQuantity(input);
      return;
    }

    if (input.name !== 'updates[]' || !input.dataset.index) return;

    const { inDrawer, lineRoot, line } = cartLineContext(input);
    if (!Number.isInteger(line) || line < 1) return;

    event.stopPropagation();
    event.stopImmediatePropagation();

    const committed = numberValue(input.dataset.shrineCommittedQuantity, numberValue(input.defaultValue, 1));
    if (!input.dataset.shrineCommittedQuantity) input.dataset.shrineCommittedQuantity = String(committed);
    const quantity = normalizeQuantity(input, input.value);
    input.value = String(quantity);
    clearCartErrors(line, inDrawer);
    queueCartChange({ line, quantity, input, lineRoot, inDrawer, committed });
  }, true);

  document.addEventListener('click', (event) => {
    const submitButton = event.target.closest('form[data-type="add-to-cart-form"] button[name="add"]');
    if (!submitButton) return;
    const form = submitButton.closest('form[data-type="add-to-cart-form"]');
    const input = productInputForForm(form);
    if (input) syncProductQuantity(input);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const input = productInputForForm(form);
    if (input) syncProductQuantity(input);
  }, true);

  const initializeProductQuantities = (root = document) => {
    root.querySelectorAll?.('input.quantity__input[name="quantity"]:not([name="updates[]"])').forEach((input) => {
      if (!input.closest('cart-drawer, cart-items')) syncProductQuantity(input);
    });
  };

  const releaseDrawerLock = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.classList.contains('active')) return;

    [document.body, document.documentElement].forEach((node) => {
      if (!node) return;
      node.classList.remove(...LOCK_CLASSES);
      if (node.style.overflow === 'hidden' || node.style.overflow === 'clip') node.style.removeProperty('overflow');
      if (node.style.touchAction === 'none') node.style.removeProperty('touch-action');
    });

    if (document.activeElement?.closest?.('cart-drawer')) {
      document.activeElement.blur?.();
      if (typeof window.removeTrapFocus === 'function') {
        try {
          window.removeTrapFocus();
        } catch (error) {
        }
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeProductQuantities(document), { once: true });
  } else {
    initializeProductQuantities(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeProductQuantities(event.target));
  document.addEventListener('click', (event) => {
    if (event.target.closest('cart-drawer .drawer__close, cart-drawer .cart-drawer__overlay')) {
      [450, 800, 1200].forEach((delay) => window.setTimeout(releaseDrawerLock, delay));
    }
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      [450, 800, 1200].forEach((delay) => window.setTimeout(releaseDrawerLock, delay));
    }
  }, true);
  window.addEventListener('pageshow', releaseDrawerLock);
})();
